import { buildCommittedLedger } from "./capacity.js";
import { addDays, calendarDaysBetween, compareISO, isWorkingDay, startOfWeek } from "./dates.js";
import type { CommittedJob, ISODate, MachineHours, ScheduleResult } from "./types.js";

/**
 * Read models over an engine timeline for the drawer, the new job preview and the calendar.
 * They only re-read dates the engine already worked out; nothing here schedules.
 */

export type PlanItemKind = "machining" | "courier_out" | "process" | "courier_back";

/** One row of "The plan": a machining step, or one leg of a post-process step. Dates are inclusive. */
export interface PlanItem {
  kind: PlanItemKind;
  stepIndex: number;
  start: ISODate;
  end: ISODate;
  /** Post-process name, for `process` items. */
  name?: string;
  /** Machining hours, for `machining` items. */
  hours?: number;
  /** A `machining` item's hours on each machine, one entry per machine. */
  machines?: MachineHours[];
  /** Working days for machining and couriers, calendar days at the vendor. */
  days: number;
}

/**
 * The timeline as rows, first to last. A post-process step becomes courier out, the time at the
 * vendor and courier back. A courier leg of zero days is left out. Each leg runs from its start up
 * to the day before the next leg starts (a courier leg, to its last working day before then);
 * the engine's own windows share their boundary days.
 */
export function planItems(result: ScheduleResult, workingDays: readonly number[]): PlanItem[] {
  const items: PlanItem[] = [];
  for (const seg of result.timeline) {
    if (seg.kind === "machining") {
      items.push({ kind: "machining", stepIndex: seg.stepIndex, start: seg.start, end: seg.end, hours: seg.hours, machines: seg.machines, days: seg.workingDays });
      continue;
    }
    const legs: Array<[PlanItemKind, { start: ISODate; end: ISODate }]> = [
      ["courier_out", seg.transitOut],
      ["process", seg.lead],
      ["courier_back", seg.transitBack],
    ];
    for (const [kind, window] of legs) {
      const length = calendarDaysBetween(window.start, window.end);
      if (kind !== "process" && length === 0) continue;
      let end = length === 0 ? window.start : addDays(window.end, -1);
      // Couriers only move on working days, so a leg over a weekend ends on its last working day.
      if (kind !== "process") while (end > window.start && !isWorkingDay(end, workingDays)) end = addDays(end, -1);
      const days = kind === "process" ? length : countWorkingDays(window.start, end, workingDays);
      items.push({ kind, stepIndex: seg.stepIndex, start: window.start, end, days, ...(kind === "process" ? { name: seg.name } : {}) });
    }
  }
  return items;
}

/** What happens on one calendar day of a plan: an item (by index into `planItems`), the due date, or nothing. */
export type PlanSlot = { kind: PlanItemKind; item: number } | { kind: "due" } | null;

/** A run of consecutive days with the same slot, for drawing one bar. */
export interface PlanRun {
  slot: Exclude<PlanSlot, null>;
  start: ISODate;
  end: ISODate;
  /** Index of `start` within the strip's days. */
  offset: number;
  length: number;
}

export interface PlanStrip {
  /** Every calendar day from the latest start to the effective due date. */
  days: ISODate[];
  slots: PlanSlot[];
  runs: PlanRun[];
}

/**
 * Day-by-day picture of a plan. Machining fills the days that carry hours, couriers fill working
 * days only, time at the vendor fills every calendar day (D4). The effective due date is shown as
 * "due" unless work is planned on it, which happens when machining is the last step.
 */
export function planStrip(result: ScheduleResult, workingDays: readonly number[]): PlanStrip {
  const items = planItems(result, workingDays);
  const first = result.latestStartDate;
  const last = result.effectiveDueDate;
  const days: ISODate[] = [];
  for (let d = first; compareISO(d, last) <= 0; d = addDays(d, 1)) days.push(d);
  const index = new Map(days.map((d, i) => [d, i]));
  const slots: PlanSlot[] = days.map(() => null);

  items.forEach((item, i) => {
    if (item.kind === "machining") {
      const seg = result.timeline.find((s) => s.stepIndex === item.stepIndex);
      const dates = seg?.kind === "machining" && seg.allocations.length > 0 ? seg.allocations.map((a) => a.date) : [item.start];
      for (const date of dates) setSlot(index.get(date), { kind: "machining", item: i });
      return;
    }
    // A zero-day vendor stay has no days of its own.
    if (item.kind === "process" && item.days === 0) return;
    for (let d = item.start; compareISO(d, item.end) <= 0; d = addDays(d, 1)) {
      if (item.kind !== "process" && !isWorkingDay(d, workingDays)) continue;
      setSlot(index.get(d), { kind: item.kind, item: i });
    }
  });
  const dueIndex = index.get(last);
  if (dueIndex !== undefined && slots[dueIndex] === null) slots[dueIndex] = { kind: "due" };

  return { days, slots, runs: toRuns(days, slots) };

  function setSlot(i: number | undefined, slot: Exclude<PlanSlot, null>) {
    if (i !== undefined) slots[i] = slot;
  }
}

function toRuns(days: ISODate[], slots: PlanSlot[]): PlanRun[] {
  const runs: PlanRun[] = [];
  slots.forEach((slot, i) => {
    if (!slot) return;
    const prev = runs[runs.length - 1];
    if (prev && prev.offset + prev.length === i && sameSlot(prev.slot, slot)) {
      prev.length++;
      prev.end = days[i]!;
    } else {
      runs.push({ slot, start: days[i]!, end: days[i]!, offset: i, length: 1 });
    }
  });
  return runs;
}

function sameSlot(a: Exclude<PlanSlot, null>, b: Exclude<PlanSlot, null>): boolean {
  return a.kind === b.kind && (a.kind === "due" || (b.kind !== "due" && a.item === b.item));
}

function countWorkingDays(from: ISODate, to: ISODate, workingDays: readonly number[]): number {
  let n = 0;
  for (let d = from; compareISO(d, to) <= 0; d = addDays(d, 1)) if (isWorkingDay(d, workingDays)) n++;
  return n;
}

export interface WeekLoad {
  /** Monday of the week. */
  weekStart: ISODate;
  hours: number;
}

/**
 * Committed machining hours in each week starting `from`'s week, for `weeks` weeks: the whole
 * shop, or one machine when `machineId` is given. Uses the same ledger as the engine, so the
 * bars match what scheduling sees (D6, D12, D49).
 */
export function weeklyLoad(committed: readonly CommittedJob[], from: ISODate, weeks: number, machineId?: string): WeekLoad[] {
  const ledger = buildCommittedLedger(committed);
  const machines = machineId === undefined ? [...ledger.values()] : [ledger.get(machineId) ?? new Map<ISODate, number>()];
  const first = startOfWeek(from);
  return Array.from({ length: weeks }, (_, w) => {
    const weekStart = addDays(first, w * 7);
    let hours = 0;
    for (const days of machines) for (let d = 0; d < 7; d++) hours += days.get(addDays(weekStart, d)) ?? 0;
    return { weekStart, hours };
  });
}
